import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import api from "../../api/api";

const APPROVAL_FILTERS = [
  { label: "All statuses", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Disapproved", value: "disapproved" },
];

const ROLE_FILTERS = [
  { label: "All roles", value: "all" },
  { label: "User", value: "user" },
  { label: "Admin", value: "admin" },
];

const APPROVAL_FIELD_CANDIDATES = [
  "IsApproved",
  "isApproved",
  "Approved",
  "approved",
];

const APPROVAL_STATUS_FIELD_CANDIDATES = [
  "ApprovalStatus",
  "approvalStatus",
  "Status",
  "status",
];

function pickValue(record, keys, fallback = "") {
  for (const key of keys) {
    const value = record?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return fallback;
}

function normalizeRole(value) {
  const nextValue = (value || "").toString().trim().toLowerCase();
  if (nextValue === "admin") {
    return "admin";
  }
  return "user";
}

function normalizeApprovalStatus(record) {
  const statusValue = pickValue(record, APPROVAL_STATUS_FIELD_CANDIDATES, "");
  const loweredStatus = statusValue.toString().trim().toLowerCase();

  if (["approved", "active"].includes(loweredStatus)) {
    return "approved";
  }

  if (
    ["disapproved", "rejected", "inactive", "blocked"].includes(loweredStatus)
  ) {
    return "disapproved";
  }

  const approvedValue = pickValue(record, APPROVAL_FIELD_CANDIDATES, null);

  if ([true, 1, "1", "true", "yes"].includes(approvedValue)) {
    return "approved";
  }

  if ([false, 0, "0", "false", "no"].includes(approvedValue)) {
    return "disapproved";
  }

  return "pending";
}

function getApprovalChipColor(status) {
  if (status === "approved") {
    return "success";
  }

  if (status === "disapproved") {
    return "error";
  }

  return "warning";
}

function toDisplayStatus(status) {
  if (status === "approved") {
    return "Approved";
  }

  if (status === "disapproved") {
    return "Disapproved";
  }

  return "Pending";
}

function normalizeUser(user) {
  const firstName = pickValue(user, ["FirstName", "firstName"]);
  const lastName = pickValue(user, ["LastName", "lastName"]);
  const fullName =
    `${firstName} ${lastName}`.trim() ||
    pickValue(user, ["Name", "name"], "Unknown user");
  const department = pickValue(user, ["Department", "department"], "-");
  const role = normalizeRole(pickValue(user, ["Role", "role"], "user"));
  const email = pickValue(user, ["Email", "email"], "-");
  const mobileNumber = pickValue(user, ["MobileNumber", "mobileNumber"], "-");
  const passedOutYear = pickValue(
    user,
    ["YearOfPassedOut", "yearOfPassedOut", "PassedOutYear", "passedOutYear"],
    "-",
  );

  return {
    raw: user,
    id: pickValue(user, ["UserId", "userId", "Id", "id"]),
    fullName,
    firstName,
    lastName,
    email,
    mobileNumber,
    department,
    passedOutYear,
    role,
    approvalStatus: normalizeApprovalStatus(user),
  };
}

function buildApprovalPayload(nextStatus) {
  return {
    IsApproved: nextStatus === "approved",
  };
}

function SummaryCard({ label, value, tone }) {
  const backgroundMap = {
    neutral: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
    warning: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
    success: "linear-gradient(135deg, #ecfdf5 0%, #dcfce7 100%)",
    danger: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        background: backgroundMap[tone] || backgroundMap.neutral,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={800} color="#0f172a">
        {value}
      </Typography>
    </Paper>
  );
}

function Users() {
  const currentUser = useSelector((state) => state.user.user) || {};
  const token =
    currentUser.token || currentUser.accessToken || currentUser.jwtToken || "";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [approvalFilter, setApprovalFilter] = useState("pending");
  const [roleFilter, setRoleFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [pendingActionUserId, setPendingActionUserId] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadUsers() {
      try {
        setLoading(true);
        setError("");
        const response = await api.getAlumniUsers(token);
        const data = response?.data || response || [];
        const userList = Array.isArray(data) ? data : [];

        if (active) {
          setUsers(userList.map(normalizeUser));
        }
      } catch (loadError) {
        if (active) {
          setError(
            loadError?.response?.data?.message ||
              loadError?.message ||
              "Unable to load users.",
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadUsers();

    return () => {
      active = false;
    };
  }, [token]);

  const availableDepartments = useMemo(() => {
    const departmentSet = new Set();

    users.forEach((user) => {
      if (user.department && user.department !== "-") {
        departmentSet.add(user.department);
      }
    });

    return Array.from(departmentSet).sort((left, right) =>
      left.localeCompare(right),
    );
  }, [users]);

  const filteredUsers = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();

    return users.filter((user) => {
      const matchesSearch =
        !searchTerm ||
        [user.fullName, user.email, user.mobileNumber, user.department]
          .join(" ")
          .toLowerCase()
          .includes(searchTerm);
      const matchesApproval =
        approvalFilter === "all" || user.approvalStatus === approvalFilter;
      const matchesRole = roleFilter === "all" || user.role === roleFilter;
      const matchesDepartment =
        departmentFilter === "all" || user.department === departmentFilter;

      return (
        matchesSearch && matchesApproval && matchesRole && matchesDepartment
      );
    });
  }, [approvalFilter, departmentFilter, roleFilter, search, users]);

  const summary = useMemo(() => {
    return users.reduce(
      (acc, user) => {
        acc.total += 1;
        if (user.approvalStatus === "approved") {
          acc.approved += 1;
        } else if (user.approvalStatus === "disapproved") {
          acc.disapproved += 1;
        } else {
          acc.pending += 1;
        }
        return acc;
      },
      { total: 0, pending: 0, approved: 0, disapproved: 0 },
    );
  }, [users]);

  const selectableUsers = useMemo(
    () => filteredUsers.filter((user) => Boolean(user.id)),
    [filteredUsers],
  );

  const allVisibleSelected =
    selectableUsers.length > 0 &&
    selectableUsers.every((user) => selectedUserIds.includes(String(user.id)));
  const someVisibleSelected =
    selectableUsers.some((user) => selectedUserIds.includes(String(user.id))) &&
    !allVisibleSelected;

  useEffect(() => {
    setSelectedUserIds((prev) =>
      prev.filter((id) => users.some((user) => String(user.id) === id)),
    );
  }, [users]);

  const handleApprovalChange = async (user, nextStatus) => {
    if (!user.id || !token) {
      setError("Missing user identifier or session token.");
      return;
    }

    try {
      setPendingActionUserId(String(user.id));
      setError("");
      setMessage("");
      const response = await api.updateUserApproval(
        user.id,
        buildApprovalPayload(nextStatus),
        token,
      );
      const responseData = response?.data || response || {};
      const updatedUser =
        responseData.user ||
        responseData.data?.user ||
        responseData.result?.user;

      setUsers((prev) =>
        prev.map((entry) => {
          if (String(entry.id) !== String(user.id)) {
            return entry;
          }

          if (updatedUser) {
            return normalizeUser(updatedUser);
          }

          return {
            ...entry,
            approvalStatus: nextStatus,
            raw: {
              ...entry.raw,
              IsApproved: nextStatus === "approved",
              ApprovalStatus: nextStatus,
            },
          };
        }),
      );
      setMessage(
        responseData.message ||
          `${user.fullName} marked as ${toDisplayStatus(nextStatus).toLowerCase()}.`,
      );
    } catch (actionError) {
      setError(
        actionError?.response?.data?.message ||
          actionError?.message ||
          "Unable to update the user approval status.",
      );
    } finally {
      setPendingActionUserId("");
    }
  };

  const handleToggleUserSelection = (userId) => {
    const nextUserId = String(userId);
    setSelectedUserIds((prev) =>
      prev.includes(nextUserId)
        ? prev.filter((id) => id !== nextUserId)
        : [...prev, nextUserId],
    );
  };

  const handleToggleVisibleUsers = () => {
    const visibleIds = selectableUsers.map((user) => String(user.id));

    setSelectedUserIds((prev) => {
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleIds.includes(id));
      }

      return Array.from(new Set([...prev, ...visibleIds]));
    });
  };

  const handleBulkApprovalChange = async (nextStatus) => {
    if (!token) {
      setError("Missing session token.");
      return;
    }

    if (selectedUserIds.length === 0) {
      setError("Select at least one user.");
      return;
    }

    try {
      setIsBulkUpdating(true);
      setError("");
      setMessage("");
      const payload = {
        UserIds: selectedUserIds.map((id) => Number(id)),
        ...buildApprovalPayload(nextStatus),
      };
      const response = await api.bulkUpdateUserApproval(payload, token);
      const responseData = response?.data || response || {};
      const updatedUsers = responseData.users || [];
      const updatedUserMap = new Map(
        updatedUsers.map((user) => [
          String(pickValue(user, ["Id", "id", "UserId", "userId"])),
          normalizeUser(user),
        ]),
      );

      setUsers((prev) =>
        prev.map((user) => updatedUserMap.get(String(user.id)) || user),
      );
      setSelectedUserIds([]);
      setMessage(
        responseData.message ||
          `${responseData.updatedCount || selectedUserIds.length} users updated successfully.`,
      );
    } catch (bulkError) {
      setError(
        bulkError?.response?.data?.message ||
          bulkError?.message ||
          "Unable to bulk update user approval.",
      );
    } finally {
      setIsBulkUpdating(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ color: "#0f172a" }}>
              Users
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.75 }}>
              Review registrations, filter the list, and approve or disapprove
              access.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <SummaryCard
                label="Total users"
                value={summary.total}
                tone="neutral"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <SummaryCard
                label="Pending approval"
                value={summary.pending}
                tone="warning"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <SummaryCard
                label="Approved"
                value={summary.approved}
                tone="success"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <SummaryCard
                label="Disapproved"
                value={summary.disapproved}
                tone="danger"
              />
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 4,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              boxShadow: "0 16px 40px rgba(15, 23, 42, 0.05)",
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Typography variant="h6" fontWeight={700}>
                  Filters
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={isBulkUpdating || selectedUserIds.length === 0}
                    onClick={() => handleBulkApprovalChange("approved")}
                  >
                    {isBulkUpdating
                      ? "Updating..."
                      : `Bulk Approve (${selectedUserIds.length})`}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={isBulkUpdating || selectedUserIds.length === 0}
                    onClick={() => handleBulkApprovalChange("disapproved")}
                  >
                    Bulk Disapprove
                  </Button>
                </Stack>
              </Stack>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Search users"
                    placeholder="Search by name, email, department"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                  <TextField
                    fullWidth
                    select
                    label="Approval"
                    value={approvalFilter}
                    onChange={(event) => setApprovalFilter(event.target.value)}
                  >
                    {APPROVAL_FILTERS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                  <TextField
                    fullWidth
                    select
                    label="Role"
                    value={roleFilter}
                    onChange={(event) => setRoleFilter(event.target.value)}
                  >
                    {ROLE_FILTERS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    select
                    label="Department"
                    value={departmentFilter}
                    onChange={(event) =>
                      setDepartmentFilter(event.target.value)
                    }
                  >
                    <MenuItem value="all">All departments</MenuItem>
                    {availableDepartments.map((department) => (
                      <MenuItem key={department} value={department}>
                        {department}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          {message ? <Alert severity="success">{message}</Alert> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              border: "1px solid rgba(15, 23, 42, 0.08)",
              boxShadow: "0 18px 40px rgba(15, 23, 42, 0.05)",
            }}
          >
            {loading ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ py: 10 }}
              >
                <CircularProgress />
              </Stack>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allVisibleSelected}
                          indeterminate={someVisibleSelected}
                          onChange={handleToggleVisibleUsers}
                          disabled={
                            selectableUsers.length === 0 || isBulkUpdating
                          }
                          inputProps={{
                            "aria-label": "Select all visible users",
                          }}
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Year</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                          <Typography variant="body1" fontWeight={600}>
                            No users match the current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((user) => {
                        const actionInProgress =
                          pendingActionUserId === String(user.id);

                        return (
                          <TableRow key={user.id || user.email}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedUserIds.includes(
                                  String(user.id),
                                )}
                                onChange={() =>
                                  handleToggleUserSelection(user.id)
                                }
                                disabled={!user.id || isBulkUpdating}
                                inputProps={{
                                  "aria-label": `Select ${user.fullName}`,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.25}>
                                <Typography fontWeight={700}>
                                  {user.fullName}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  User ID: {user.id || "-"}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.department}</TableCell>
                            <TableCell>{user.passedOutYear}</TableCell>
                            <TableCell>
                              <Chip
                                label={user.role === "admin" ? "Admin" : "User"}
                                color={
                                  user.role === "admin" ? "primary" : "default"
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={toDisplayStatus(user.approvalStatus)}
                                color={getApprovalChipColor(
                                  user.approvalStatus,
                                )}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1}
                                justifyContent="flex-end"
                              >
                                <Button
                                  variant="contained"
                                  color="success"
                                  disabled={
                                    actionInProgress ||
                                    user.approvalStatus === "approved"
                                  }
                                  onClick={() =>
                                    handleApprovalChange(user, "approved")
                                  }
                                >
                                  {actionInProgress ? "Updating..." : "Approve"}
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  disabled={
                                    actionInProgress ||
                                    user.approvalStatus === "disapproved"
                                  }
                                  onClick={() =>
                                    handleApprovalChange(user, "disapproved")
                                  }
                                >
                                  Disapprove
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}

export default Users;
